import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Divider, Grid, IconButton, InputLabel, Stack, Tooltip, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { getErrMsg, toastNeg, toastPos } from '../../../common/toastHelper'
import { IDropdownSkill, INewSkill } from '../../../interfaces/skills/ISkills'
import { useStore } from '../../../Provider'
import { ExitConfirmation } from '../../OPcomponents/ExitConfirmation'
import { OPCheckBox } from '../../OPcomponents/OPCheckBox'
import { OPLoadingCircle } from '../../OPcomponents/OPLoadingcircle'
import { OPMultiline } from '../../OPcomponents/OPMultiline'
import { OPSkillbar } from '../../OPcomponents/OPSkillbar'
import { OPSkillsCombobox } from '../../OPcomponents/OPSkillsCombobox'
import { skillSchema } from '../../OPcomponents/ValidationSchemas'
import '../../styles/grid.scss'
import { OPSkillsButtons } from '../OPSkillsButtons'
import '../profile.scss'

const AddNewSkill = (): JSX.Element => {
  const { t, i18n } = useTranslation()
  const rootStore = useStore()
  const { validatedUser } = rootStore.loginStore

  const { filteredSkills, loadingInitial, getAllSkills, postNewSkillToUser, showNewSkillModalHandler, postNewSkillProject, allSkills } =
    rootStore.skillStore
  const { getAllUserSkills, loadingSkills, userSkills, userObject, getUserOverview } = rootStore.personStore
  const { projects, getAllProjects } = rootStore.projectStore

  type INewSkillWithProject = INewSkill & { projectId: number }
  type SkillOption = IDropdownSkill & { categoryName?: string }

  const initValues: INewSkillWithProject = {
    personId: validatedUser,
    skillId: -1,
    skillLevel: 0,
    interest: 0,
    comment: '',
    projectId: -1,
    isKeySkill: true,
  }

  const filteredDropdownSkills: SkillOption[] = useMemo(() => {
    if (!filteredSkills) return []
    const ownedSkillIds = new Set((userSkills ?? []).map((s) => s.skillId))

    return filteredSkills
      .filter((i) => !ownedSkillIds.has(i.skill.skillId))
      .map((s) => ({
        key: s.skill.skillId,
        value: String(s.skill.skillId),
        text: i18n.language === 'en' && s.skill.skillNameEng?.length ? s.skill.skillNameEng : s.skill.skillNameFi,
        categoryName: s.skill.categoryName,
      }))
      .sort((a, b) => {
        const catA = a.categoryName || ''
        const catB = b.categoryName || ''
        const catCmp = catA.localeCompare(catB, i18n.language, { sensitivity: 'base' })
        if (catCmp !== 0) return catCmp
        return a.text.localeCompare(b.text, i18n.language, { sensitivity: 'base' })
      })
  }, [filteredSkills, userSkills, i18n.language])

  // Build project options once per relevant change
  const filteredDropdownProjects = useMemo(() => {
    return projects
      .filter((proj) => Boolean(proj.userHas))
      .map((project) => ({
        key: project.project.projectId,
        value: String(project.project.projectId),
        text: project.project.name + (project.project.customer ? ` (${project.project.customer})` : ''),
        isEmploymentProject: project.isEmployerJobTitlesProject,
      }))
      .sort((a, b) => {
        if (a.isEmploymentProject !== b.isEmploymentProject) {
          return a.isEmploymentProject ? -1 : 1
        }
        return a.text.localeCompare(b.text, i18n.language, { sensitivity: 'base' })
      })
  }, [projects, i18n.language])
  useEffect(() => {
    getAllSkills(validatedUser)
  }, [getAllSkills, getAllUserSkills, validatedUser])

  useEffect(() => {
    if (!userObject || Object.keys(userObject).length === 0) void getUserOverview(validatedUser)
    if (!userObject || Object.keys(userObject).length === 0) void getAllProjects(validatedUser)
  }, [getUserOverview, userObject, validatedUser, getAllProjects])

  const onSubmit = async (values: INewSkillWithProject) => {
    try {
      await postNewSkillToUser(values, validatedUser)
      // Link the newly added skill to the selected project
      await postNewSkillProject({
        userId: validatedUser,
        projectId: values.projectId,
        skillId: values.skillId,
      })
      await getAllUserSkills(validatedUser)
      toastPos(t('Toasts.SkillUserAdd'), true)
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.SkillAddError'), true)
    } finally {
      showNewSkillModalHandler()
    }
  }

  return (
    <>
      {loadingInitial && loadingSkills ? (
        <OPLoadingCircle />
      ) : (
        <Formik
          enableReinitialize
          initialValues={initValues}
          onSubmit={(values) => onSubmit(values)}
          validationSchema={skillSchema.concat(
            Yup.object({
              projectId: Yup.number().min(1).required(),
            }),
          )}
        >
          {({ isValid, dirty, values, setFieldValue, handleChange, isSubmitting, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <ExitConfirmation when={dirty} />
              <Grid container justifyContent='space-between' rowSpacing={2} columnSpacing={8}>
                {/* Left column: project + skill + level */}
                <Grid container item xs={12} md={6} spacing={2}>
                  <Grid item xs={12}>
                    <OPSkillsCombobox
                      name='projectId'
                      label={`${t('Projects.EmployerOrProject')} *`}
                      optionList={filteredDropdownProjects}
                      curState={undefined}
                      groupBy={(opt) => (opt?.isEmploymentProject ? t('Projects.Employer') : t('Projects.Project'))}
                      onChange={(_e, val) => {
                        if (val) setFieldValue('projectId', val.key) // keep number in form state
                      }}
                      onDelete={() => {
                        setFieldValue('projectId', null)
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction='row' alignItems='center' spacing={0.5}>
                      <InputLabel className='comboBoxLabel'>{t('Discussions.GoalSkill')} *</InputLabel>
                      <Tooltip title={t('Skills.AddNewSkillInfo')} arrow placement='right'>
                        <IconButton size='small'>
                          <InfoOutlinedIcon fontSize='small' color='info' />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <OPSkillsCombobox
                      placeholder={`${t('Discussions.GoalSkill')}\u2026`}
                      multiple={false}
                      optionList={filteredDropdownSkills}
                      label=''
                      name='skillId'
                      curState={undefined}
                      groupBy={(opt) => opt?.categoryName || t('Skills.Category')}
                      onChange={(_e, val) => {
                        if (val) setFieldValue('skillId', val.key)
                      }}
                      onDelete={() => {
                        setFieldValue('skillId', null)
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} className='griditem--formitem__formtoppadding'>
                    <OPSkillbar
                      name={'skillLevel'}
                      hasLabel
                      label={`${t('Skills.Level')} *`}
                      value={values.skillLevel ?? 0}
                      onChange={(_e, val) => setFieldValue('skillLevel', val)}
                      showLevelText
                    />
                  </Grid>

                  <Grid item xs={12} className='griditem--formitem__formtoppadding'>
                    <OPCheckBox
                      name='isKeySkill'
                      label={t('Skills.KeySkill')}
                      value={values.isKeySkill || false}
                      onChangeState={(_, checked) => setFieldValue('isKeySkill', checked)}
                    />
                  </Grid>

                  {/* Selected skill details */}
                  {values.skillId > 0 && (
                    <Grid item xs={12}>
                      {(() => {
                        const selected = allSkills.find((s) => s.skill.skillId === values.skillId)?.skill
                        if (!selected) return null
                        const parent = selected.parentSkillId
                          ? allSkills.find((s) => s.skill.skillId === selected.parentSkillId)?.skill
                          : undefined
                        const description =
                          i18n.language === 'en' && selected.descriptionEng && selected.descriptionEng.length > 0
                            ? selected.descriptionEng
                            : selected.skillDescription
                        return (
                          <Box>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant='subtitle2' gutterBottom>
                              {t('Skills.SkillInfo')}
                            </Typography>

                            <Stack direction='row' spacing={1} alignItems='baseline' sx={{ flexWrap: 'wrap' }}>
                              <Typography variant='caption' sx={{ fontWeight: 600 }}>
                                {t('Skills.Category')}:
                              </Typography>
                              <Typography variant='body2'>{selected.categoryName}</Typography>
                            </Stack>

                            <Stack direction='row' spacing={1} alignItems='baseline' sx={{ flexWrap: 'wrap' }}>
                              <Typography variant='caption' sx={{ fontWeight: 600 }}>
                                {t('Skills.ParentSkill')}:
                              </Typography>
                              <Typography variant='body2'>
                                {parent
                                  ? i18n.language === 'en' && parent.skillNameEng?.length
                                    ? parent.skillNameEng
                                    : parent.skillNameFi
                                  : null}
                              </Typography>
                            </Stack>

                            <Stack direction='row' spacing={1} alignItems='baseline' sx={{ flexWrap: 'wrap' }}>
                              <Typography variant='caption' sx={{ fontWeight: 600 }}>
                                {t('Skills.Description')}:
                              </Typography>
                              <Typography variant='body2'>{description && description.trim().length > 0 ? description : null}</Typography>
                            </Stack>
                          </Box>
                        )
                      })()}
                    </Grid>
                  )}
                </Grid>

                {/* Right column: comment */}
                <Grid item xs={12} md={6}>
                  <OPMultiline
                    name='comment'
                    placeholder={`${t('Skills.FurtherInfo')}\u2026`}
                    label={t('Skills.FurtherInfo')}
                    hasCounter
                    maxChars={250}
                    minRows={8}
                    value={values.comment}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <OPSkillsButtons
                    noPadding
                    onClose={showNewSkillModalHandler}
                    onSubmit={handleSubmit}
                    bothDisabled={isSubmitting}
                    disabled={!(isValid && dirty) || isSubmitting}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </>
  )
}

export default observer(AddNewSkill)
